import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  homeVisitClinicalRecords: [],
  currentRecord: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Clinical Records
export const fetchHomeVisitClinicalRecords = createAsyncThunk(
  'homeVisitClinicalRecords/fetchHomeVisitClinicalRecords',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId ? `/home-visit-clinical-records/${patientId}` : '/home-visit-clinical-records';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Home Visit Clinical records'
      );
    }
  }
);

// Fetch a single Clinical Record
export const fetchHomeVisitClinicalRecord = createAsyncThunk(
  'homeVisitClinicalRecords/fetchHomeVisitClinicalRecord',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/home-visit-clinical-records/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch record'
      );
    }
  }
);

// Create a new Clinical Record
export const createHomeVisitClinicalRecord = createAsyncThunk(
  'homeVisitClinicalRecords/createHomeVisitClinicalRecord',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/home-visit-clinical-records', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create record'
      );
    }
  }
);

// Update an existing Clinical Record
export const updateHomeVisitClinicalRecord = createAsyncThunk(
  'homeVisitClinicalRecords/updateHomeVisitClinicalRecord',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/home-visit-clinical-records/${id}`, formData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update record'
      );
    }
  }
);

// Delete a Clinical Record
export const deleteHomeVisitClinicalRecord = createAsyncThunk(
  'homeVisitClinicalRecords/deleteHomeVisitClinicalRecord',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/home-visit-clinical-records/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete record'
      );
    }
  }
);

// ==================== Slice ====================
const homeVisitClinicalRecordSlice = createSlice({
  name: 'homeVisitClinicalRecords',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentRecord: (state, action) => {
      state.currentRecord = action.payload;
    },
    clearCurrentRecord: (state) => {
      state.currentRecord = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchHomeVisitClinicalRecords.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHomeVisitClinicalRecords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.homeVisitClinicalRecords = action.payload;
      })
      .addCase(fetchHomeVisitClinicalRecords.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchHomeVisitClinicalRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHomeVisitClinicalRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentRecord = action.payload;
      })
      .addCase(fetchHomeVisitClinicalRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createHomeVisitClinicalRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createHomeVisitClinicalRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.homeVisitClinicalRecords.unshift(action.payload);
      })
      .addCase(createHomeVisitClinicalRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateHomeVisitClinicalRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateHomeVisitClinicalRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload;
        const index = state.homeVisitClinicalRecords.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.homeVisitClinicalRecords[index] = updated;
        }
        if (state.currentRecord?.id === updated.id) {
          state.currentRecord = updated;
        }
      })
      .addCase(updateHomeVisitClinicalRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteHomeVisitClinicalRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteHomeVisitClinicalRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.homeVisitClinicalRecords = state.homeVisitClinicalRecords.filter(
          (d) => d.id !== action.payload
        );
        if (state.currentRecord?.id === action.payload) {
          state.currentRecord = null;
        }
      })
      .addCase(deleteHomeVisitClinicalRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentRecord,
  clearCurrentRecord,
} = homeVisitClinicalRecordSlice.actions;

export default homeVisitClinicalRecordSlice.reducer;
